import React, { useState, useCallback } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { Button } from 'react-bootstrap'
import PopUp from 'reusableComponents/PopUp'
import { InputField } from 'reusableComponents/InputField'
import { ReactComponent as UserCheck } from 'assets/svg/user-check.svg'
import SVGIcon from 'reusableComponents/SVGIcon'
import { colors } from 'globalStyles'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { phoneUpdateVerifyOTP, resendOTPForUser, verifyOTP } from 'api/shipping'
import { updateOtpVerifiedTimestamp } from 'api/accounts'
import { getDOMEncodeString } from 'utils/helperFunctions'

const StyledLinkButton = styled(Button)`
  padding: 0 0.75rem;
  &.btn-check:focus + &.btn,
  &.btn:focus {
    box-shadow: 0 0 0 0.25rem rgb(0 0 0 / 0%);
  }
`

const StyledVerifyContent = styled.div`
  .form-floating {
    display: inline-block;
    margin: 7px 0;
    width: 295px;
  }
`

const VerifyOTPModal = ({
  show,
  onHide,
  phoneNumber,
  isUpdatePhoneSuccess,
  resetProfileData,
  showModalFor,
}) => {
  const { t, i18n } = useTranslation()
  const { language } = i18n

  const [verificationCode, setVerificationCode] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const formatNumber = (num) => {
    return num.replace(/[^\d]/g, '')
  }

  const last4DigitsPhoneNumber = phoneNumber.slice(-4)

  const verificationCodeChangeHandler = (event) => {
    setVerificationCode(event.target.value)
    setErrorMessage('') // Clear error when user types
  }

  const onActionVerifyHandler = useCallback(async () => {
    try {
      if (verificationCode.length === 0) {
        setErrorMessage(t('subscription.verification_code_empty_error'))
        return
      }
      if (verificationCode.length < 6) {
        setErrorMessage(t('subscription.verification_code_invalid_error'))
        return
      }

      const formattedNumber = formatNumber(phoneNumber)
      if (showModalFor === 'updatePhone') {
        const response = await phoneUpdateVerifyOTP(formattedNumber, verificationCode, language)
        if (response.data?.errorCode === 'ERR100') {
          if (typeof isUpdatePhoneSuccess === 'function') isUpdatePhoneSuccess(false)
          setErrorMessage(
            response.data.message || t('subscription.verification_code_invalid_response')
          )
          return
        }
        if (response.status === 200) {
          if (typeof isUpdatePhoneSuccess === 'function') isUpdatePhoneSuccess(true)
        } else if (typeof isUpdatePhoneSuccess === 'function') isUpdatePhoneSuccess(false)
        onHide(false)
      } else if (showModalFor === 'verifyOtpEvery6Months') {
        const otpVerifyResponse = await verifyOTP(formattedNumber, verificationCode, language)
        if (otpVerifyResponse.status === 200) {
          const updateTimestampResponse = await updateOtpVerifiedTimestamp()
          if (updateTimestampResponse.status === 200) {
            onHide('success')
          }
        }
      }
    } catch (error) {
      if (typeof isUpdatePhoneSuccess === 'function') isUpdatePhoneSuccess(false)

      const responseData = error?.response?.data
      const responseCode = responseData?.error?.[0]?.response
      if (responseData?.errorCode === 'ERR100') {
        setErrorMessage(
          responseData.message || t('subscription.verification_code_invalid_response')
        )
      } else if (responseCode === 'VERIFICATION_CODE_DOES_NOT_EXISTS') {
        setErrorMessage(t('forgotemail.verification_code_expired_error'))
      } else {
        setErrorMessage(t('subscription.verification_code_invalid_response'))
      }
    }
  }, [phoneNumber, verificationCode, language, isUpdatePhoneSuccess, onHide, t, showModalFor])

  const onActionCloseHandler = () => {
    if (showModalFor === 'updatePhone') {
      setVerificationCode('')
      setErrorMessage('')
      if (typeof isUpdatePhoneSuccess === 'function') isUpdatePhoneSuccess(false)
      onHide(false)
      resetProfileData()
    } else if (showModalFor === 'verifyOtpEvery6Months') {
      onHide()
    }
  }

  const resendVerificationCodeHandler = () => {
    const formattedNumber = formatNumber(phoneNumber)
    resendOTPForUser(formattedNumber, language)
      .then((result) => {
        if (result.status === 200 && typeof result.data.data !== 'undefined') {
          if (
            result.data.data.length > 0 &&
            result.data.data[0].response === 'MAX_VERIFICATIONCODE_RESEND_LIMIT'
          ) {
            setErrorMessage(t('subscription.verification_code_max_attempts_error'))
          }
        }
      })
      .catch(() => {
        setErrorMessage(t('subscription.general_error_message'))
      })
  }

  return (
    <PopUp
      title={t('verificationPopup.verification_popup_title')}
      show={show}
      size="md"
      icon={getDOMEncodeString(<SVGIcon Icon={UserCheck} color={colors.primary_black} />)}
      iconWidth="45px"
      primaryButton={
        <Button variant="secondary" onClick={onActionCloseHandler}>
          {t('verificationPopup.cancel_button')}
        </Button>
      }
      secondaryButton={
        <Button variant="primary" onClick={onActionVerifyHandler}>
          {t('verificationPopup.verify_button')}
        </Button>
      }
      onHide={onHide}
      isUpdatePhoneSuccess={isUpdatePhoneSuccess}
    >
      <StyledVerifyContent>
        {t('verificationPopup.verification_code_popup_message', {
          phoneNumber: last4DigitsPhoneNumber,
        })}
        <InputField
          type="text"
          id="input-veri-code"
          label={t('subscription.verification_code_empty_error')}
          onChange={verificationCodeChangeHandler}
          value={verificationCode}
          maxLength={6}
          isRequired
          autoComplete="off"
          isInvalid={!!errorMessage}
          errorText={errorMessage}
        />
        <div>
          <Trans
            t={t}
            i18nKey="subscription.no_sms_received"
            components={[
              <StyledLinkButton variant="link" onClick={resendVerificationCodeHandler} />,
            ]}
          />
        </div>
      </StyledVerifyContent>
    </PopUp>
  )
}

VerifyOTPModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func,
  isUpdatePhoneSuccess: PropTypes.func,
  resetProfileData: PropTypes.func,
  phoneNumber: PropTypes.string.isRequired,
  showModalFor: PropTypes.string.isRequired,
}

VerifyOTPModal.defaultProps = {
  onHide: null,
  isUpdatePhoneSuccess: null,
  resetProfileData: null,
}

export default VerifyOTPModal
